<template>
  <div class="meeting-card">
    <section
      class="top d-flex justify-content-between align-items-start flex-nowrap p-4"
      :class="{ 'bg-lightest-gray': expanded }"
    >
      <div class="flex-grow-0 c-charcoal">
        <div class="time mb-2">{{ startTime }} - {{ endTime }}</div>
        <HMTag v-if="meeting.is_cancelled" status="cancelled">
          {{ $t('MARKETING.LABELS.CANCELLED') }}
        </HMTag>
        <div v-else class="pointer c-coral" @click="copyMeetingLink">
          {{ $t('MARKETING.LABELS.COPY_MEETING_LINK') }}
          <i class="far fa-link c-coral ml-2" />
        </div>
      </div>
      <div class="flex-grow-1 text-truncate px-4 c-charcoal">
        <h4 class="who mb-2">
          {{ meeting.full_name }}
        </h4>
        <span> {{ meeting.meeting_type.name }}</span>
      </div>
      <div
        class="expand flex-grow-0 text-right pointer"
        @click="expanded = !expanded"
      >
        {{ $t('MARKETING.LABELS.DETAILS') }}
        <i
          class="fal fa-chevron-down ml-2 rotate c-brand-color"
          :class="{ down: expanded }"
        ></i>
      </div>
    </section>
    <b-collapse role="tabpanel" v-model="expanded">
      <hr class="mt-0" />
      <section class="bottom d-flex flex-row align-items-start">
        <div class="actions p-4">
          <div v-if="!meeting.is_cancelled" class="d-flex flex-column">
            <HMButton class="mb-3" color="outline-brand" @click="startMeeting">
              <i class="fas fa-camcorder c-brand-color mr-2"></i>
              {{ $t('MARKETING.BUTTONS.START_MEETING') }}
            </HMButton>
            <HMButton
              class="mb-3"
              color="outline-brand"
              @click="showRescheduleModal"
            >
              <i class="fas fa-repeat c-brand-color mr-2"></i>
              {{ $t('MARKETING.BUTTONS.RESCHEDULE_MEETING') }}
            </HMButton>
            <HMButton color="outline-brand" @click="showCancelMeetingModal">
              <i class="far fa-times c-brand-color mr-2"></i>
              {{ $t('MARKETING.BUTTONS.CANCEL_MEETING') }}
            </HMButton>
          </div>
        </div>
        <div class="answers py-4 d-flex flex-column">
          <div class="question smaller">
            {{ $t('GENERAL.LABEL.EMAIL') }}
          </div>
          <div class="answer smaller">
            <a :href="`mailto:${meeting.email}`">{{ meeting.email }}</a>
          </div>
          <div
            class="answers mt-3"
            v-for="question in questions"
            :key="question.position"
          >
            <div class="question smaller">
              {{ question.label }}
            </div>
            <div class="answer smaller">
              {{ question.answer }}
            </div>
          </div>
        </div>
      </section>
    </b-collapse>
  </div>
</template>

<script>
import moment from 'moment/moment';
import MeetingRescheduleModal from './MeetingRescheduleModal';
import HMDialog from '../common/HMDialog';
import { errorToMessage } from '../../../core/utils/errors';
import { mapGetters } from 'vuex';
import { getStudentViewURL } from '../../router/custom-domain-utils';
import { notify } from '../../utils/notify';

export default {
  name: 'MeetingCard',
  props: {
    meeting: {
      type: Object,
    },
  },
  computed: {
    ...mapGetters({
      studioURL: 'user/studioURL',
    }),
    startTime() {
      return this.$formatDate(this.meeting.start_time, 'time');
    },
    endTime() {
      return this.$formatDate(
        moment(this.meeting.start_time)
          .add(this.meeting.duration_minutes, 'minutes')
          .toDate(),
        'time'
      );
    },
    questions() {
      if (!this.meeting?.form) return [];
      return (
        [...this.meeting.form].sort((p1, p2) => p1.position - p2.position) ?? []
      );
    },
  },
  data() {
    return {
      expanded: false,
    };
  },
  methods: {
    copyMeetingLink() {
      const url = getStudentViewURL(this.studioURL, '/meeting', null);
      const value = `${url}/${this.meeting.unique_id}`;
      this.$copyText(value).then(() => {
        notify(this.$t('PRODUCTS.TOAST.COPIED'), value);
      });
    },
    startMeeting() {
      const windowReference = window.open();
      try {
        windowReference.location = this.meeting.event.zoom_start_url;
      } catch (error) {
        windowReference.close();
      }
    },
    showRescheduleModal() {
      this.$modal.show(
        MeetingRescheduleModal,
        {
          meetingType: this.meeting.meeting_type,
          studioURL: this.studioURL,
          currentDateTime: moment(this.meeting.start_time).toDate(),
          onMeetingRescheduled: async (newSelectedDate, timezone) => {
            await this.reschedule(newSelectedDate, timezone);
          },
        },
        {
          classes: ['vm--booking-modal'],
        }
      );
    },
    async reschedule(newSelectedDate, timezone) {
      this.isRescheduling = true;
      try {
        this.meeting = await this.$store.dispatch(
          'marketing/reschedulePublicMeeting',
          {
            studioURL: this.studioURL,
            meetingUniqueId: this.meeting.unique_id,
            newStartTime: newSelectedDate,
            timezone,
          }
        );
        this.$emit('meetingRescheduled');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to reschedule meeting',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      } finally {
        this.isRescheduling = false;
      }
    },
    async showCancelMeetingModal() {
      this.$modal.show(HMDialog, {
        title: this.$t('MARKETING.TITLES.CANCEL_MEETING'),
        text: this.$t('MARKETING.TEXT.CONFIRM_CANCEL_MEETING', {
          fullName: this.meeting.full_name,
        }),
        confirmButton: this.$t('GENERAL.BUTTON.CANCEL_MEETING'),
        confirm: async () => {
          await this.cancelMeeting();
        },
      });
    },
    async cancelMeeting() {
      try {
        await this.$store.dispatch('marketing/cancelMeeting', this.meeting.id);
        this.$emit('meetingCancelled');
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to cancel meeting',
          text: errorToMessage(error?.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';

.meeting-card {
  border-radius: $border-radius;
  border: 1px solid $medium-gray;
}

.answers {
  .question {
    color: $dark-gray;
  }
  .answer {
    color: $charcoal;
  }
}

.top {
  .time {
    height: 1.5rem;
  }
  .who {
    height: 1.5rem;
    line-height: 1.3;
  }
}

.line-through {
  text-decoration: line-through;
}
</style>

<style lang="scss">
.vm--booking-modal {
  max-width: 1280px !important;
}
</style>
